<template>
  <div>
    <v-alert
      v-if="!isVodEvent"
      class="pa-2 mb-4"
      v-model="alert"
      color="primary"
      icon="info"
      outline
      dismissible
    >
      {{ $t('screener.monetization.single_ticket.info') }}
    </v-alert>

    <h3 v-if="!isVodEvent"
      class="grey--text text--darken-3"
      v-html="$t('screener.save.monetization.headline')"
    ></h3>
    <v-checkbox v-if="!isVodEvent" :rules="isVodEvent ?[rules.required] :[]" v-model="localMonetizationEnabled" color="primary">
      <template v-slot:label>
        <div>
          <b>{{ $t('screener.save.monetization.enable') }}</b>
        </div>
      </template>
      <template v-slot:append>
        <v-tooltip top color="white">
          <template v-slot:activator="{on}">
            <v-icon v-on="on">mdi-information-outline</v-icon>
          </template>
          <div class="grey--text text--darken-3 body-1">
            {{ $t('screener.monetization.paid_screening.edit_warning') }}
          </div>
        </v-tooltip>
      </template>
    </v-checkbox>
    <template v-if="localMonetizationEnabled">
      <v-layout row wrap class="mb-4">
        <v-flex xs12>
          <div v-if="!isVodEvent" class="subheading greyDarken3--text">
            <b>{{ $t('screener.save.monetization.select_sale_type') }}</b>
          </div>
          <!-- Single Sale Selection-->
          <v-checkbox
            v-if="!isVodEvent"
            single-line
            hide-details
            :rules="isVodEvent ?[rules.required] :[]"
            v-model="
              localMonetization[
                isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
              ][0].type
            "
            :label="'Single Sale'"
            :value="isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE"
            color="primary"
          ></v-checkbox>
          <!-- Price Input -->
          <v-text-field
            class="mt-3"
            v-if="
              localMonetization[
                isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
              ][0].type
            "
            v-model="
              localMonetization[
                isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
              ][0].price
            "
            :prefix="
              getCurrencySymbol(
                localMonetization[
                  isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
                ][0].currency,
              )
            "
            :label="$t('screener.save.monetization.price')"
            :rules="
              localMonetization[
                isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
              ][0].type
                ? [rules.required, rules.floating_number, rules.greaterThanZero]
                : []
            "
            placeholder="0.00"
            :suffix="
              localMonetization[
                isVodEvent ?DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE :DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
              ].currency
            "
            :hint="$t('screener.monetization.paid_screening.edit_warning')"
            persistent-hint
          >
            <template v-slot:append>
              <v-tooltip top color="white">
                <template v-slot:activator="{on}">
                  <v-icon v-on="on">mdi-information-outline</v-icon>
                </template>
                <div
                  class="grey--text text--darken-3 body-1"
                  v-html="$t('screener.save.monetization.currency.tip')"
                ></div>
              </v-tooltip>
            </template>
          </v-text-field>
          <SystemShareView
            class="my-3"
            v-if="
                localMonetization[
                  DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
                ][0].type
            "
            :price="
              localMonetization[
                DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
              ][0].price
            "
            :currency="currentCompany.currency"
            :commissionPercentage="hasReducedPriceBilling?currentCompany.reduced_price_commission_percentage : currentCompany.commission_percentage"
          ></SystemShareView>

          <!-- Group Sale Selection -->
          <v-checkbox
            v-if="!isVodEvent"
            single-line
            hide-details
            v-model="show_group_pricing"
            :value="DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE"
            :label="'Group Sale'"
            color="primary"
          ></v-checkbox>

          <!-- Pricing plans selection -->
          <v-autocomplete
            v-if="
              show_group_pricing ||
                localMonetization[
                  DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
                ].length
            "
            v-model="
              localMonetization[
                DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
              ]
            "
            :items="pricing_plans"
            prepend-icon="mdi-cards"
            multiple
            :rules="[rules.required]"
            chips
            :label="$t('screener.monetization.group_ticket.title')"
            item-text="name"
            color="primary"
            item-value="id"
            return-object
          >
            <template v-slot:selection="data">
              <v-chip
                close
                class="chip--select-multi primary--text grey lighten-3"
                @input="removeSelectedPlan(data.item.id)"
              >
                {{ data.item.name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <template>
                <v-list-tile-content>
                  <v-list-tile-title
                    v-html="data.item.name"
                  ></v-list-tile-title>
                  <v-list-tile-sub-title
                    v-html="data.item.description"
                  ></v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </template>
          </v-autocomplete>
        </v-flex>
      </v-layout>
      <v-divider class="pb-3"></v-divider>
    </template>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import {currencySymbol, getPricingListFromApi} from '@/mixins/monetization';
import rules from '@/validation';
import RadioInput from '@/components/RadioInput';
import DEFINES from '@/defines';
import SystemShareView from '@/components/screener/monetization/SystemShareView';

export default {
  name: 'Monetization',
  props: ['monetization', 'monetizationEnabled', 'isVodEvent','hasReducedPriceBilling'],
  data() {
    return {
      rules,
      show_group_pricing: false,
      alert: true,
    };
  },
  components: {
    RadioInput,
    SystemShareView,
  },
  computed: {
    ...mapState({
      pricing_plans: (state) => state.monetization.list,
    }),
    ...mapGetters('company', {
      currentCompany: 'getSelectedCompany',
    }),
    localMonetizationEnabled: {
      get: function() {
        return this.monetizationEnabled;
      },
      set: function(value) {
        this.$emit('update:monetizationEnabled', value);
      },
    },
    localMonetization: {
      get: function() {
        return this.monetization;
      },
      set: function(value) {
        this.$emit('update:monetization', value);
      },
    },
  },
  mixins: [currencySymbol, getPricingListFromApi],
  mounted() {

    // Getting plans from API. Always load latest plans
    // this.getPricingListFromApi();

    //Enable Monetization in case of VOD events
    if(this.isVodEvent){
      this.localMonetizationEnabled = this.isVodEvent ?true :false;
    }
  },
  methods: {
    removeSelectedPlan(pricing_id) {
      this.localMonetization[
        DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
      ] = this.localMonetization[
        DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
      ].filter((pricing) => {
        return pricing_id !== pricing.id;
      });
    },
  },
};
</script>

<style></style>
